import type { QueryClient } from '@tanstack/react-query'

import type { Page } from '@/api/client'
import type { components } from '@/api/schema'
import { marketEndpoints } from '@/api/endpointCache'
import { getProposedPage } from '@/api/proposedEndpoint'
import {
  LIVE_MAX_LIMIT,
  MOVERS_MAX_LIMIT,
  SEARCH_MAX_LIMIT,
  type MarketRange,
} from '@/features/market/api/marketApi'
import {
  backendSectorKey,
  breadthFromWire,
  displayDate,
  displayTime,
  marketStateIsOpen,
  pageTotal,
  sparklineValues,
} from '@/features/market/api/marketMappers'
import { sortTableRows } from '@/lib/tableSorting'
import {
  gainersLosersSchema,
  liveMarketPageSchema,
  priceRowListSchema,
  stockPricesPageSchema,
  priceHistorySchema,
  week52PageSchema,
  week52RowSchema,
  type GainersLosers,
  type GainersLosersQuery,
  type LiveMarketPage,
  type LiveMarketQuery,
  type PriceRow,
  type StockPricesPage,
  type PriceHistory,
  type StockPricesQuery,
  type Week52Page,
  type Week52Query,
  type Week52Row,
} from '@/schemas/prices'
import { sliceTablePage } from '@/schemas/table'

type LiveWire = components['schemas']['LiveRow']
type MoverWire = components['schemas']['MoverRow']

/**
 * The series as this mapper must survive it: a deploy older than mm-be 5f4ed8f
 * sends no `summary` (see `priceHistorySchema`). The generated type is today's
 * contract, and reading `summary.*` off it would be a TypeError there, failing
 * the Overview chart along with the table.
 */
type PriceSeriesWire = Omit<components['schemas']['PriceSeries'], 'summary'> & {
  summary?: components['schemas']['PriceSeries']['summary'] | null
}

/**
 * `stock_price` stores a price it never recorded as 0, and NEPSE has no price of
 * 0 — mm-be's chart feed drops such a session for exactly that reason
 * (`services/chart/bars.py`, `_ohlc`). This feed does not, so a zero here is a
 * gap: read as one, it draws a dash instead of "0.00" in a column of real
 * prices, and cannot drag a range's low to 0.
 */
const recordedPrice = (price: string | null): string | null =>
  price === null || /^-?0+(\.0+)?$/.test(price.trim()) ? null : price

/**
 * `/market/stocks/{symbol}/price-history/` as the app's series.
 *
 * Every figure the table prints is carried through as sent — the session's own
 * % change, volume and turnover, and the range's summary strip — apart from a
 * price recorded as 0 (see `recordedPrice`). Nothing is derived here, so the
 * table, the strip and the sparkline cannot disagree about a session by
 * computing it three ways.
 */
export const mapPriceHistory = (series: PriceSeriesWire) => {
  const summary = series.summary ?? null
  return {
    symbol: series.symbol,
    name: series.name,
    range: series.range,
    points: series.points.map((point) => ({
      date: point.t,
      value: point.value,
      open: recordedPrice(point.open),
      high: recordedPrice(point.high),
      low: recordedPrice(point.low),
      close: recordedPrice(point.close),
      percentChange: point.percent_change,
      volume: point.volume,
      turnover: point.turnover,
    })),
    summary:
      summary === null
        ? null
        : {
            high: recordedPrice(summary.high),
            low: recordedPrice(summary.low),
            averageVolume: summary.average_volume,
            averageTurnover: summary.average_turnover,
          },
  }
}

/**
 * Wire rows to price rows, parsed one at a time.
 *
 * Nothing here may throw: a value `money()` would reject is handed to the
 * schema as-is, so `priceRowListSchema` drops that one row and the table still
 * renders every other row. A malformed sparkline is the exception that never
 * reaches the schema at all — `sparklineValues` empties that one cell first, and
 * the schema's `.catch` is only the net under it.
 */
const toPriceRows = (live: readonly LiveWire[], activity: readonly MoverWire[]): PriceRow[] => {
  const activityBySymbol = new Map(activity.map((row) => [row.key, row]))
  return priceRowListSchema.parse(
    live.map((row) => {
      const mover = activityBySymbol.get(row.key)
      // `mover` is absent when this symbol is outside the movers-by-volume
      // window, and `volume` does not exist independently of it.
      return {
        symbol: row.key,
        name: row.name,
        sparkline: sparklineValues(row.sparkline),
        range: {
          low: Number(row.low),
          high: Number(row.high),
          open: Number(row.open),
          // `null` when the session has no published close yet; DayRangeBar
          // already renders nothing for a missing close rather than a
          // misleading dot at the LTP.
          close: row.close === null ? null : Number(row.close),
        },
        volumeKitta: mover?.volume ?? null,
        ltp: row.ltp,
        percentChange: row.change_percent,
      }
    }),
  )
}

const sortPriceRows = (rows: readonly PriceRow[], query: LiveMarketQuery | StockPricesQuery) =>
  sortTableRows(rows, query, {
    symbol: (row) => row.symbol,
    sparkline: (row) => row.percentChange,
    range: (row) => row.range.high - row.range.low,
    close: (row) => row.ltp,
    ltp: (row) => row.ltp,
    percentChange: (row) => row.percentChange,
    volume: (row) => row.volumeKitta,
  })

const matchesBreadth = (percentChange: string, breadth: LiveMarketQuery['breadth']): boolean => {
  if (breadth === undefined) return true
  const change = Number(percentChange)
  return breadth === 'rose' ? change > 0 : breadth === 'fell' ? change < 0 : change === 0
}

/**
 * Whether mm-be serves a week's gainers and losers yet. False until the PROPOSED
 * `period=1w` on `/market/movers/` ships (`mm-market-2026-10-28/asks/mv1.md`): until then no
 * request is made, and the "1 week" option on Gainers & losers shows the no-data state. Flip this
 * one constant when it ships.
 */
export const GAINERS_LOSERS_WEEK_AVAILABLE = false as boolean

/**
 * One column of Gainers & losers: a page of rows, and how many names moved that way in the whole
 * market.
 *
 * Takes the WIRE PAGE, not its rows, because the count behind the pager is a fact about the
 * market and only `meta` carries it. `/market/movers/` merges `PageMeta` (mm-be
 * api/v5/market/views.py `MoversView`), and mm-be computes `total` from `qs.count()` BEFORE the
 * slice (services/market/repository.py `get_movers`), so the page it sends never changes the
 * count.
 *
 * Reading `rows.length` instead would print the request's own size as the market's. `pageTotal`'s
 * `meta.count` fallback is the page length, so a deployment that somehow sent no `total` degrades
 * to one page rather than to zero.
 */
const moverColumn = (page: Page<MoverWire>, pageSize: number) => ({
  rows: page.rows.slice(0, pageSize).map((row) => ({
    symbol: row.key,
    name: row.name,
    ltp: row.ltp,
    percentChange: row.change_percent,
  })),
  total: pageTotal(page.meta, page.rows.length),
})

/**
 * One end of the movers list for a period. Today only the day is typed on the generated client;
 * the week is the proposed `period=1w`, which goes through the untyped seam and only once
 * `GAINERS_LOSERS_WEEK_AVAILABLE` is true.
 */
const moversFor = (
  query: GainersLosersQuery,
  type: 'gainers' | 'losers',
  client: QueryClient | undefined,
): Promise<Page<MoverWire>> =>
  query.period === '1w'
    ? getProposedPage<MoverWire>('/api/v5/market/movers/', {
        type,
        period: '1w',
        page: query.page,
        page_size: query.pageSize,
      })
    : marketEndpoints(client).movers({ type, page: query.page, page_size: query.pageSize })

export const pricesService = {
  liveMarket: async (query: LiveMarketQuery, client?: QueryClient): Promise<LiveMarketPage> => {
    const api = marketEndpoints(client)
    const [live, activity, summary, status] = await Promise.all([
      api.live(
        LIVE_MAX_LIMIT,
        query.sector === undefined ? undefined : backendSectorKey(query.sector),
      ),
      api.movers({ type: 'volume', limit: MOVERS_MAX_LIMIT }),
      api.summary(),
      api.status(),
    ])
    const rows = toPriceRows(live.rows, activity.rows).filter((row) =>
      matchesBreadth(row.percentChange, query.breadth),
    )

    return liveMarketPageSchema.parse({
      ...sliceTablePage(sortPriceRows(rows, query), query),
      asOf: displayDate(summary.as_of),
      status: marketStateIsOpen(status.state) ? 'open' : 'closed',
      statusTime: displayTime(summary.as_of),
      breadth: breadthFromWire(summary.breadth),
      mitraSays: 'Quotes come from the latest market snapshot supplied by the backend.',
    })
  },

  stockPrices: async (query: StockPricesQuery, client?: QueryClient): Promise<StockPricesPage> => {
    const api = marketEndpoints(client)
    const [live, activity, search] = await Promise.all([
      api.live(LIVE_MAX_LIMIT),
      api.movers({ type: 'volume', limit: MOVERS_MAX_LIMIT }),
      query.query === undefined || query.query.trim() === ''
        ? Promise.resolve(null)
        : // `SEARCH_MAX_LIMIT`, not 200: `/market/search/` caps `limit` at 50 and
          // rejects anything larger. The old `200` was a 400 on every keystroke,
          // and because it sat inside this `Promise.all` it failed the whole
          // page — "Couldn't load stock prices" for a symbol that exists.
          api.search(query.query.trim(), SEARCH_MAX_LIMIT),
    ])
    const matches = search === null ? null : new Set(search.rows.map((row) => row.key))
    const priced = toPriceRows(live.rows, activity.rows)
    const rows = priced.filter((row) => matches === null || matches.has(row.symbol))

    return stockPricesPageSchema.parse({
      ...sliceTablePage(sortPriceRows(rows, query), query),
      mitraSays: 'A price is the latest print, not a verdict. Open a company to inspect it.',
    })
  },

  week52: async (query: Week52Query, client?: QueryClient): Promise<Week52Page> => {
    const api = marketEndpoints(client)
    // ONE call, at the endpoint's ceiling: every row now carries both bounds and
    // both distances, so `basis` only picks the order mm-be ranks in — and the
    // sort below replaces that order whichever way it is asked.
    const [ranked, sector] = await Promise.all([
      api.fiftyTwoWeek('high'),
      query.sector === undefined
        ? Promise.resolve(null)
        : api.heatmap(backendSectorKey(query.sector)),
    ])
    const allowed = sector === null ? null : new Set(sector.rows.map((row) => row.key))
    // Every company arrives in the one call, so a search is a filter over them, not a request.
    const needle = query.query?.trim().toLowerCase() ?? ''
    const rows = ranked.rows
      .filter((row) => allowed === null || allowed.has(row.key))
      .filter(
        (row) =>
          needle === '' ||
          row.key.toLowerCase().includes(needle) ||
          row.name.toLowerCase().includes(needle),
      )
      .map((row) => ({
        symbol: row.key,
        name: row.name,
        ltp: row.ltp,
        high52: row.high,
        low52: row.low,
        // `null` when mm-be sends no distance, never a fabricated 0%.
        offHighPercent: row.off_high_percent,
        offLowPercent: row.off_low_percent,
      }))
    // Nearest first, nulls last whichever tab is open: the high tab ends at the
    // most negative distance, the low tab at the largest positive one.
    const nearest =
      query.sort === 'nearest-high'
        ? sortTableRows(
            rows,
            { sortBy: 'offHigh', sortDesc: true },
            { offHigh: (row) => row.offHighPercent },
          )
        : sortTableRows(
            rows,
            { sortBy: 'offLow', sortDesc: false },
            { offLow: (row) => row.offLowPercent },
          )

    return week52PageSchema.parse({
      ...sliceTablePage(
        sortTableRows(nearest, query, {
          symbol: (row) => row.symbol,
          ltp: (row) => row.ltp,
          high52: (row) => row.high52,
          low52: (row) => row.low52,
          offHigh: (row) => row.offHighPercent,
          offLow: (row) => row.offLowPercent,
        }),
        query,
      ),
      intro: 'Distance from each company’s published 52-week high or low.',
      // The mock's line under the pager (static copy; the table is ordered by the distance the tab
      // is named for until a head is clicked).
      footnote: `Sorted by distance ${
        query.sort === 'nearest-high' ? 'below the high' : 'above the low'
      }. Distance from a 52-week print is a fact about the range, not a target or a cue.`,
      mitraSays: 'Distance from a yearly high does not make a company cheap by itself.',
    })
  },

  gainersLosers: async (
    query: GainersLosersQuery,
    client?: QueryClient,
  ): Promise<GainersLosers> => {
    // The week has no feed yet: a caller reaches this only when the flag is on, so a stray call is
    // an error, never a request to an endpoint that does not exist.
    if (query.period === '1w' && !GAINERS_LOSERS_WEEK_AVAILABLE) {
      throw new Error("mm-be does not serve a week's gainers and losers yet")
    }
    const [gainers, losers] = await Promise.all([
      moversFor(query, 'gainers', client),
      moversFor(query, 'losers', client),
    ])
    return gainersLosersSchema.parse({
      gainers: moverColumn(gainers, query.pageSize),
      losers: moverColumn(losers, query.pageSize),
      footnote: `${query.period.toUpperCase()} moves use the backend’s latest market snapshot.`,
      mitraSays: 'Large moves attract attention; they do not explain company value on their own.',
    })
  },

  /**
   * NEPSE as a 52-week row, for the line the mock pins above both tabs: the index's own value
   * against its 52-week high and low, off each worked out here (`/market/indices/` serves the
   * bounds but no distance). `null` when the feed has no NEPSE row or no bound, so the table
   * simply starts at the companies.
   */
  nepseWeek52: async (client?: QueryClient): Promise<Week52Row | null> => {
    const { rows } = await marketEndpoints(client).indices()
    const nepse = rows.find((row) => row.key === 'NEPSE')
    if (
      nepse === undefined ||
      nepse.fifty_two_week_high === null ||
      nepse.fifty_two_week_low === null
    ) {
      return null
    }
    const value = Number(nepse.value)
    const high = Number(nepse.fifty_two_week_high)
    const low = Number(nepse.fifty_two_week_low)
    if (![value, high, low].every((figure) => Number.isFinite(figure) && figure > 0)) return null
    return week52RowSchema.parse({
      symbol: 'NEPSE',
      name: 'NEPSE Index',
      ltp: nepse.value,
      high52: nepse.fifty_two_week_high,
      low52: nepse.fifty_two_week_low,
      offHighPercent: (((value - high) / high) * 100).toFixed(2),
      offLowPercent: (((value - low) / low) * 100).toFixed(2),
    })
  },

  priceHistory: async (
    symbol: string,
    range: MarketRange,
    client?: QueryClient,
  ): Promise<PriceHistory> => {
    const series = await marketEndpoints(client).priceHistory(symbol, range)
    return priceHistorySchema.parse(mapPriceHistory(series))
  },
}
